import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {
  EMAIL_REGEX_V2,
  PHONE_REGEX_WITH_PASTE_ENABLED,
  THAI_ADDRESS_BRANCH_ID_REGEX,
  THAI_ADDRESS_TAX_ID_REGEX
} from 'constants/index';
import {StoreArea} from 'types';

import {capitalizeFirstLetter} from './helper';

export const isValidTaxId = (thaiId: string): boolean => {
  try {
    const m = thaiId?.trim().match(/(\d{12})(\d)/);
    if (!m) {
      console.warn('Bad input from user, invalid thaiId=', thaiId);
      throw new Error('thai-id-must-be-13-digits');
    }

    if (thaiId[0] == '0') {
      throw new Error('thai-id-should-not-start-with-zero');
    }

    if (thaiId.trim().length !== 13) {
      throw new Error('thai-id-must-be-13-length');
    }

    const digits = m[1].split('');
    const sum = digits.reduce((total: number, digit: string, i: number) => {
      return total + (13 - i) * +digit;
    }, 0);
    const lastDigit = `${(11 - (sum % 11)) % 10}`;
    const inputLastDigit = m[2];
    if (lastDigit !== inputLastDigit) {
      console.warn('Bad input from user, invalid checksum thaiId=', thaiId);
      throw new Error('thai-id-checksum-mismatched');
    }
    return true;
  } catch (error) {
    return false;
  }
};

export const isValidEmail = (emailStringArg: string | undefined | null, validForUndefinedOrNull = false) => {
  try {
    if (emailStringArg === null || emailStringArg === undefined) {
      return validForUndefinedOrNull;
    }
    const emailInputString = emailStringArg.trim();
    if (emailInputString.split(' ').length !== 1) {
      throw new Error('multiple-strings');
    }
    const validEmailString = emailInputString.match(EMAIL_REGEX_V2);

    if (validEmailString?.[0]?.length !== emailInputString.length) {
      throw new Error('contains-invalid-characters');
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const isURL = (input: string) => {
  try {
    new URL(input);
    return true;
  } catch (_) {
    if (input.startsWith('www.')) {
      return true;
    } else {
      return false;
    }
  }
};

export const useAddressFormValidationSchema = (st: StoreArea[]) => {
  const {t} = useTranslation();

  const mandatoryString = yup.string().ensure().trim().required(t('message.fieldIsMandatory'));

  return yup.object().shape({
    firstName: mandatoryString,
    lastName: mandatoryString,
    mobile: yup
      .string()
      .required(t('message.fieldIsMandatory'))
      .matches(PHONE_REGEX_WITH_PASTE_ENABLED, {
        excludeEmptyString: true,
        message: t('message.invalidPhone')
      }),
    company: yup.string().ensure().trim().nullable(),
    address1: yup
      .string()
      .ensure()
      .trim()
      .required(t('message.fieldIsMandatory'))
      .test('no-links', t('message.linksNotAllowed'), (value) => !isURL(value)),
    address2: yup
      .string()
      .ensure()
      .trim()
      .nullable()
      .test('no-links', t('message.linksNotAllowed'), (value) => !isURL(value || '')),
    zip: yup
      .string()
      .ensure()
      .trim()
      .required(t('message.fieldIsMandatory'))
      .matches(/\d{5}/, capitalizeFirstLetter(t('invalidZipCode')))
      .test(
        'zip-supported',
        t('zipcodeIsNotSupportedByMakroPro'),
        (value) => !value || (Array.isArray(st) && st.some((e) => e.z === value)) // Only run if zip is provided and is in the supported list
      ),
    province_id: yup.string().ensure().trim().required(t('message.fieldIsMandatory')),
    district_id: yup.string().ensure().trim().required(t('message.fieldIsMandatory')),
    subDistrict_id: yup.string().ensure().trim().required(t('message.fieldIsMandatory')),
    deliveryInstructions: yup.string().ensure().trim().nullable(),
    isDefault: yup.boolean(),
    longitude: yup.number().nullable(),
    latitude: yup.number().nullable(),
    user_longitude: yup.number().nullable(),
    user_latitude: yup.number().nullable(),
    branchId: yup
      .string()
      .ensure()
      .trim()
      .matches(THAI_ADDRESS_BRANCH_ID_REGEX, {
        excludeEmptyString: true,
        message: t('message.invalidBranchId')
      }),
    email: yup
      .string()
      .trim()
      .nullable()
      .test({
        name: 'is-valid-email-id',
        message: t('message.invalidEmail'),
        test: (value) => isValidEmail(value, true),
        exclusive: true
      }),
    taxId: yup
      .string()
      .ensure()
      .trim()
      .matches(THAI_ADDRESS_TAX_ID_REGEX, {
        excludeEmptyString: true,
        message: t('message.taxIdRequired')
      })
      .nullable()
  });
};
export const useUserDetailFormValidationSchema = () => {
  const {t} = useTranslation();

  return yup.object().shape({
    userDisplayName: yup.string().ensure().trim().required(t('message.fieldIsMandatory')),
    userMobile: yup
      .string()
      .required(t('message.fieldIsMandatory'))
      .matches(PHONE_REGEX_WITH_PASTE_ENABLED, {
        excludeEmptyString: true,
        message: t('message.invalidPhone')
      }),
    userEmail: yup
      .string()
      .trim()
      .nullable()
      .test({
        name: 'is-valid-email-id',
        message: t('message.invalidEmail'),
        test: (value) => isValidEmail(value, true),
        exclusive: true
      })
  });
};
